import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Mail, Shield, UserCog, Check, X } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate } from '@/lib/utils';
import { USER_ROLES, type UserRole } from '@praxara/shared/constants';

export function SettingsTeamPage() {
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);

  const users = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => (await api.get('/api/admin/users')).data,
  });

  const updateUser = useMutation({
    mutationFn: async (params: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/api/admin/users/${params.id}`, params.patch),
    onSuccess: () => {
      toast.success('Member updated');
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err) => toast.error('Update failed', extractApiError(err).message),
  });

  const members = users.data?.items ?? [];
  const activeMembers = members.filter((m: Member) => m.isActive);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl">Team</h1>
          <p className="text-sm text-muted-foreground">
            Invite members, change roles, and manage account access.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus className="mr-1.5 h-4 w-4" /> Invite member
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <MiniStat label="Total members" value={members.length} />
        <MiniStat label="Active" value={activeMembers.length} />
        <MiniStat label="MFA enabled" value={activeMembers.filter((m: Member) => m.mfaEnabled).length} />
        <MiniStat
          label="Signed e-sig agreement"
          value={activeMembers.filter((m: Member) => m.signatureAgreed).length}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            5 roles: ADMIN &gt; MANAGER &gt; REVIEWER &gt; OPERATOR &gt; VIEWER.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {users.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading...</div>
          ) : members.length === 0 ? (
            <EmptyState
              icon={UserCog}
              title="No team members yet"
              description="Invite your first team member to start collaborating on regulatory workflows."
              action={{ label: 'Invite member', onClick: () => setInviteOpen(true) }}
              className="m-4"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name + email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Security</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m: Member) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      <div className="font-medium">{m.firstName} {m.lastName}</div>
                      <div className="text-xs text-muted-foreground">{m.email}</div>
                    </TableCell>
                    <TableCell>
                      <select
                        value={m.role}
                        onChange={(e) => updateUser.mutate({ id: m.id, patch: { role: e.target.value } })}
                        className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                      >
                        {USER_ROLES.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <Badge variant={m.isActive ? 'success' : 'secondary'}>
                        {m.isActive ? 'Active' : 'Deactivated'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1" title="MFA">
                          {m.mfaEnabled ? <Check className="h-3 w-3 text-emerald-600" /> : <X className="h-3 w-3 text-muted-foreground" />}
                          <Shield className="h-3 w-3" /> MFA
                        </span>
                        <span className="inline-flex items-center gap-1" title="E-sig">
                          {m.signatureAgreed ? <Check className="h-3 w-3 text-emerald-600" /> : <X className="h-3 w-3 text-muted-foreground" />}
                          e-sig
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(m.lastLoginAt)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateUser.mutate({ id: m.id, patch: { isActive: !m.isActive } })}
                      >
                        {m.isActive ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onInvited={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })} />
    </div>
  );
}

interface Member {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  mfaEnabled: boolean;
  signatureAgreed: boolean;
  lastLoginAt: string | null;
}

function MiniStat({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="font-display text-2xl">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function InviteDialog({ open, onOpenChange, onInvited }: { open: boolean; onOpenChange: (o: boolean) => void; onInvited: () => void }) {
  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'REVIEWER' as UserRole,
  });

  const invite = useMutation({
    mutationFn: async () => api.post('/api/admin/users/invite', form),
    onSuccess: (res) => {
      toast.success('Invitation sent', `${res.data.email} will receive an email with a setup link`);
      onOpenChange(false);
      setForm({ email: '', firstName: '', lastName: '', role: 'REVIEWER' });
      onInvited();
    },
    onError: (err) => toast.error('Invite failed', extractApiError(err).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite team member</DialogTitle>
          <DialogDescription>
            They&apos;ll receive an email with a link to set their password (expires in 72h).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Email</Label>
            <Input type="email" autoFocus value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="colleague@pharma.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First name</Label>
              <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </div>
            <div>
              <Label>Last name</Label>
              <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Role</Label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              {USER_ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              <Mail className="inline h-3 w-3" /> Member receives an email invitation
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => invite.mutate()} disabled={invite.isPending || !form.email || !form.firstName || !form.lastName}>
            {invite.isPending ? 'Sending...' : 'Send invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
